import { useState, useEffect } from 'react';
import { profileService } from '../lib/profile-service';
import { activityLogService } from '../lib/activity-log-service';
import { UserProfile, User } from '../types/content';

/**
 * Profile Editor Hook Options
 */
export interface UseProfileEditorOptions {
  /**
   * Firebase user ID
   */
  userId: string;
  
  /**
   * User's display name
   */
  displayName: string;
  
  /**
   * User's role for activity logging
   */
  role: string;
  
  /**
   * Initial profile data (pass userData from useAuth)
   */
  initialProfile: Partial<UserProfile> | null;
  
  /**
   * Callback to refresh user data after save
   */
  onSaved?: () => Promise<void>;
}

/**
 * Profile Editor Hook Return Value
 */
export interface UseProfileEditorReturn {
  /**
   * Current profile data being edited
   */
  profileData: UserProfile;
  
  /**
   * Whether edit mode is active
   */
  isEditing: boolean;
  
  /**
   * Whether a save operation is in progress
   */
  saving: boolean;
  
  /**
   * Error message if any
   */
  error: string;
  
  /**
   * Success message if any
   */
  success: string;
  
  /**
   * Enable edit mode
   */
  startEditing: () => void;
  
  /**
   * Cancel editing and reset to original data
   */
  cancelEditing: () => void;
  
  /**
   * Update a single profile field
   */
  updateField: <K extends keyof UserProfile>(field: K, value: UserProfile[K]) => void;
  
  /**
   * Update the profile image URL
   */
  updateProfileImage: (imageUrl: string) => void;
  
  /**
   * Save all profile changes
   */
  saveProfile: () => Promise<void>;
  
  /**
   * Clear error message
   */
  clearError: () => void;
  
  /**
   * Clear success message
   */
  clearSuccess: () => void;
}

/**
 * Profile Editor Hook
 * 
 * Manages profile editing state, validation, and persistence.
 * Handles both regular profile updates and profile image uploads.
 * 
 * @example
 * ```tsx
 * const { user, userData, refreshUserData } = useAuth();
 * 
 * const {
 *   profileData,
 *   isEditing,
 *   saving,
 *   error,
 *   success,
 *   startEditing,
 *   cancelEditing,
 *   updateField,
 *   saveProfile,
 * } = useProfileEditor({
 *   userId: user?.uid || '',
 *   displayName: userData?.displayName || '',
 *   role: userData?.role || 'writer',
 *   initialProfile: userData,
 *   onSaved: refreshUserData,
 * });
 * 
 * // Start editing
 * <button onClick={startEditing}>Edit</button>
 * 
 * // Update a field
 * <input
 *   value={profileData.displayName}
 *   onChange={(e) => updateField('displayName', e.target.value)}
 * />
 * 
 * // Save changes
 * <button onClick={saveProfile} disabled={saving}>Save</button>
 * ```
 */
export function useProfileEditor(options: UseProfileEditorOptions): UseProfileEditorReturn {
  const { userId, displayName, role, initialProfile, onSaved } = options;
  
  const [profileData, setProfileData] = useState<UserProfile>({
    displayName: initialProfile?.displayName || '',
    profileImage: initialProfile?.profileImage || '',
    bio: initialProfile?.bio || '',
    homepage: initialProfile?.homepage || '',
    mastheadName: initialProfile?.mastheadName || '',
    mastheadHandle: initialProfile?.mastheadHandle || '',
    mastheadUrl: initialProfile?.mastheadUrl || '',
    twitterHandle: initialProfile?.twitterHandle || '',
    twitterUsername: initialProfile?.twitterUsername || '',
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Store initial profile for reset functionality
  const [originalProfile, setOriginalProfile] = useState<UserProfile>({
    displayName: initialProfile?.displayName || '',
    profileImage: initialProfile?.profileImage || '',
    bio: initialProfile?.bio || '',
    homepage: initialProfile?.homepage || '',
    mastheadName: initialProfile?.mastheadName || '',
    mastheadHandle: initialProfile?.mastheadHandle || '',
    mastheadUrl: initialProfile?.mastheadUrl || '',
    twitterHandle: initialProfile?.twitterHandle || '',
    twitterUsername: initialProfile?.twitterUsername || '',
  });
  
  // Sync profile data when initialProfile changes
  // We use a stringified version to detect deep changes without causing infinite loops
  useEffect(() => {
    if (initialProfile) {
      const newProfile = {
        displayName: initialProfile.displayName || '',
        profileImage: initialProfile.profileImage || '',
        bio: initialProfile.bio || '',
        homepage: initialProfile.homepage || '',
        mastheadName: initialProfile.mastheadName || '',
        mastheadHandle: initialProfile.mastheadHandle || '',
        mastheadUrl: initialProfile.mastheadUrl || '',
        twitterHandle: initialProfile.twitterHandle || '',
        twitterUsername: initialProfile.twitterUsername || '',
      };
      setProfileData(newProfile);
      setOriginalProfile(newProfile);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userId,
    initialProfile?.displayName,
    initialProfile?.profileImage,
    initialProfile?.bio,
    initialProfile?.homepage,
    initialProfile?.mastheadName,
    initialProfile?.mastheadHandle,
    initialProfile?.mastheadUrl,
    initialProfile?.twitterHandle,
    initialProfile?.twitterUsername,
  ]);
  
  /**
   * Start editing mode
   */
  const startEditing = () => {
    setIsEditing(true);
    setError('');
    setSuccess('');
  };
  
  /**
   * Cancel editing and reset to original data
   */
  const cancelEditing = () => {
    setIsEditing(false);
    setError('');
    setSuccess('');
    
    // Reset to original profile
    setProfileData({ ...originalProfile });
  };
  
  /**
   * Update a single profile field
   */
  const updateField = <K extends keyof UserProfile>(field: K, value: UserProfile[K]) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };
  
  /**
   * Update profile image URL
   */
  const updateProfileImage = (imageUrl: string) => {
    setProfileData(prev => ({ ...prev, profileImage: imageUrl }));
  };
  
  /**
   * Track what changed for activity log
   */
  const getProfileChanges = (): Record<string, { old: any; new: any }> => {
    const changes: Record<string, { old: any; new: any }> = {};
    
    if (profileData.displayName !== originalProfile.displayName) {
      changes.displayName = { old: originalProfile.displayName, new: profileData.displayName };
    }
    if (profileData.bio !== (originalProfile.bio || '')) {
      changes.bio = { old: originalProfile.bio || '', new: profileData.bio };
    }
    if (profileData.homepage !== (originalProfile.homepage || '')) {
      changes.homepage = { old: originalProfile.homepage || '', new: profileData.homepage };
    }
    if (profileData.twitterHandle !== (originalProfile.twitterHandle || '')) {
      changes.twitterHandle = { old: originalProfile.twitterHandle || '', new: profileData.twitterHandle };
    }
    if (profileData.twitterUsername !== (originalProfile.twitterUsername || '')) {
      changes.twitterUsername = { old: originalProfile.twitterUsername || '', new: profileData.twitterUsername };
    }
    if (profileData.mastheadName !== (originalProfile.mastheadName || '')) {
      changes.mastheadName = { old: originalProfile.mastheadName || '', new: profileData.mastheadName };
    }
    if (profileData.mastheadHandle !== (originalProfile.mastheadHandle || '')) {
      changes.mastheadHandle = { old: originalProfile.mastheadHandle || '', new: profileData.mastheadHandle };
    }
    if (profileData.mastheadUrl !== (originalProfile.mastheadUrl || '')) {
      changes.mastheadUrl = { old: originalProfile.mastheadUrl || '', new: profileData.mastheadUrl };
    }

    return changes;
  };

  /**
   * The payload actually written. `profileService.updateUserProfile` spreads its argument straight
   * into `updateDoc`, so EVERY key present here lands on the user doc — including empty strings.
   *
   * The three masthead fields are therefore stripped when they are empty AND were empty/absent to
   * begin with. Two independent reasons, both load-bearing:
   *
   *  1. COST — `getMastheadIdentities()` queries `where('mastheadName','!=','')`, and Firestore `!='
   *     only returns docs where the field EXISTS. Writing `mastheadName: ''` onto every user who
   *     ever saves their profile would put the entire user table into that field's index instead of
   *     just the handful of masthead rows.
   *  2. SECURITY — firestore.rules `selfWriteMastheadOk()` denies a non-admin self-write that SETS
   *     or CHANGES any masthead field, and absent → '' IS a change. Sending the keys
   *     unconditionally would make every non-admin profile save fail permission-denied.
   *
   * A masthead member CLEARING their name still writes '' (a real change, so admin/owner-only) —
   * which drops them out of the `!= ''` query exactly as intended.
   */
  const buildSavePayload = (): UserProfile => {
    const payload: UserProfile = { ...profileData };
    for (const field of ['mastheadName', 'mastheadHandle', 'mastheadUrl'] as const) {
      if (!payload[field] && !originalProfile[field]) delete payload[field];
    }
    return payload;
  };

  /**
   * Save profile changes
   */
  const saveProfile = async () => {
    if (!userId) {
      setError('You must be logged in to update your profile.');
      return;
    }
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      // Track changes for activity log
      const changes = getProfileChanges();
      
      // Update profile
      await profileService.updateUserProfile(userId, buildSavePayload());
      
      // Log activity if there were changes
      if (Object.keys(changes).length > 0) {
        // Build minimal user object for activity logging
        const userForLog = {
          id: userId,
          displayName: displayName,
          role: role as any,
        } as User;
        
        await activityLogService.logActivity(
          'user_profile_updated',
          userForLog,
          'user',
          userId,
          displayName,
          {
            profileChanges: changes
          },
          'admin' // Admins can monitor profile changes for moderation
        );
      }
      
      // Update original profile to reflect saved state
      setOriginalProfile({ ...profileData });
      
      // Refresh the auth context with updated profile data
      if (onSaved) {
        await onSaved();
      }
      
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  /**
   * Clear error message
   */
  const clearError = () => {
    setError('');
  };
  
  /**
   * Clear success message
   */
  const clearSuccess = () => {
    setSuccess('');
  };
  
  return {
    profileData,
    isEditing,
    saving,
    error,
    success,
    startEditing,
    cancelEditing,
    updateField,
    updateProfileImage,
    saveProfile,
    clearError,
    clearSuccess,
  };
}

